import React, { FC, useState } from 'react';
import useTranslation from '../../hooks/useTranslation';
import { Box } from '@mui/material';
import { getFY, getFyNumber, updateFy } from '../../utils/calculation';
import { PartOfCrpComboBoxProps } from './PartOfCrpComboBoxProps';
import { PartOfCrp } from '../../models/project';
import CompactPillOverlay from '../CompactPillOverlay';
import { MultiSelectField } from '@ingka-group-digital/expansion-multiselect';

type DropDownElem = {
  value: string;
  label: string;
  approved: boolean;
};

const SELECTION_START_FY_SHIFT = 1;
const SELECTION_END_FY_SHIFT = 3;

const PartOfCrpComboBox: FC<PartOfCrpComboBoxProps> = ({
  partOfCrp,
  handleUpdate,
  dropDownValueFilter,
  label,
  id,
  maxDisplayItems,
}) => {
  const t = useTranslation();
  const [selectedValuesIds, setSelectedValuesIds] = useState<string[]>(() => {
    const fyArr = (partOfCrp || []).map((e) => e.fiscalYear);
    fyArr.sort((a, b) => getFyNumber(a) - getFyNumber(b));
    return fyArr;
  });

  const dropDownValues = (): DropDownElem[] => {
    const values = [];
    for (let i = SELECTION_START_FY_SHIFT; i <= SELECTION_END_FY_SHIFT; i++) {
      const fy = getFY(i);

      if (dropDownValueFilter && !dropDownValueFilter.includes(fy)) {
        continue;
      }

      const partOfCrpElem = (partOfCrp || []).find((e) => e.fiscalYear === fy);
      values.push({
        value: fy,
        label: `${fy}-${updateFy(fy, +2)}`,
        approved: partOfCrpElem !== undefined && partOfCrpElem.approved,
      });
    }
    return values;
  };

  const updateSelectedValues = (newSelections: string[]) => {
    const approvedAndExistingElements = (partOfCrp || []).filter(
      (e) => e.approved || newSelections.includes(e.fiscalYear),
    );

    const existingFYs = new Set(
      (partOfCrp || []).map((item) => item.fiscalYear),
    );
    const addedElements = newSelections
      .filter((selectedId) => !existingFYs.has(selectedId))
      .map((fiscalYear) => ({ fiscalYear }));

    const updatedPartOfCrp = [...approvedAndExistingElements, ...addedElements];
    updatedPartOfCrp.sort(
      (a, b) => getFyNumber(a.fiscalYear) - getFyNumber(b.fiscalYear),
    );

    setSelectedValuesIds(updatedPartOfCrp.map((e) => e.fiscalYear));
    handleUpdate(updatedPartOfCrp as PartOfCrp[]);
  };

  const useCompact = maxDisplayItems !== undefined;
  const selectId = id || 'part_of_crp_select';

  return (
    <Box
      sx={{
        [`& .${selectId}__value-container > div`]: {
          display: 'flex',
          'flex-wrap': 'wrap',
        },
        ...(useCompact && {
          position: 'relative',
          [`& #${selectId}-wrapper .${selectId}__multi-value`]: {
            display: 'none',
          },
        }),
        '& button > span.pill__label': {
          'font-size': '12px',
          'font-weight': '700',
          'line-height': '1.25',
        },
      }}
    >
      {useCompact && (
        <CompactPillOverlay
          selectedValues={selectedValuesIds}
          maxDisplayItems={maxDisplayItems}
        />
      )}
      <MultiSelectField
        id={selectId}
        options={dropDownValues()}
        selectedValues={selectedValuesIds}
        setSelectedValues={updateSelectedValues}
        getOptionIsDisabled={(e: DropDownElem) => e.approved}
        label={label !== undefined ? label : t('labels.partOfCrp')}
        displayCountOnly={useCompact}
      />
    </Box>
  );
};

export default PartOfCrpComboBox;
